import React from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiMapPin, FiUsers, FiCheckCircle, FiXCircle } from 'react-icons/fi';

const BranchStatusSummary = ({ branches = [] }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Calculate statistics
  const totalBranches = branches.length;
  const activeBranches = branches.filter(branch => branch.status === 'active').length;
  const inactiveBranches = totalBranches - activeBranches;
  const totalEmployees = branches.reduce((sum, branch) => sum + (branch.total_employees || 0), 0);

  return (
    <Box
      bg={bgColor}
      borderRadius="lg"
      p={6}
      border="1px solid"
      borderColor={borderColor}
      shadow="sm"
    >
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between" align="center">
          <Text fontSize="lg" fontWeight="600" color="gray.700">
            Branch Performance Summary
          </Text>
          <HStack spacing={2}>
            <FiMapPin size={20} color="#6B7280" />
            <Text fontSize="sm" color="gray.500">
              {totalBranches} Total Branches
            </Text>
          </HStack>
        </HStack>

        <SimpleGrid columns={{ base: 1, sm: 2, md: 2, lg: 4 }} spacing={4}>
          <Stat>
            <StatLabel color="gray.600">Total Branches</StatLabel>
            <StatNumber color="blue.600">{totalBranches}</StatNumber>
            <StatHelpText>All locations</StatHelpText>
          </Stat>

          <Stat>
            <StatLabel color="gray.600">Active Branches</StatLabel>
            <StatNumber color="green.600">{activeBranches}</StatNumber>
            <StatHelpText>
              <HStack spacing={1}>
                <FiCheckCircle size={12} color="#16A34A" />
                <Text fontSize="xs">Operational</Text>
              </HStack>
            </StatHelpText>
          </Stat>

          <Stat>
            <StatLabel color="gray.600">Inactive Branches</StatLabel>
            <StatNumber color="orange.600">{inactiveBranches}</StatNumber>
            <StatHelpText>
              <HStack spacing={1}>
                <FiXCircle size={12} color="#EA580C" />
                <Text fontSize="xs">Suspended</Text>
              </HStack>
            </StatHelpText>
          </Stat>

          <Stat>
            <StatLabel color="gray.600">Total Employees</StatLabel>
            <StatNumber color="purple.600">{totalEmployees}</StatNumber>
            <StatHelpText>
              <HStack spacing={1}>
                <FiUsers size={12} color="#9333EA" />
                <Text fontSize="xs">Across all branches</Text>
              </HStack>
            </StatHelpText>
          </Stat>
        </SimpleGrid>

        {inactiveBranches > 0 && (
          <Box
            bg="orange.50"
            border="1px solid"
            borderColor="orange.200"
            borderRadius="md"
            p={4}
          >
            <HStack spacing={2}>
              <FiXCircle size={16} color="#EA580C" />
              <Text fontSize="sm" color="orange.800" fontWeight="500">
                {inactiveBranches} branch{inactiveBranches > 1 ? 'es are' : ' is'} currently inactive
              </Text>
            </HStack>
            <Text fontSize="xs" color="orange.700" mt={1}>
              Inactive branches cannot be accessed by users. Activate them in Branch Management.
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default BranchStatusSummary; 